// The "editable base, fixed extension" name model, shared by every feature that edits a file's name
// without letting the user change what kind of file it is.
//
// This started as three byte-identical private copies — DetailsTableView.vue's own splitName (for the
// Name column's base/extension split), useFileManagerRename's (for the rename modal), and it would
// have been a fourth for copy. RENAME_ITEM_PLAN.md deliberately kept the first two separate and named
// the exact condition for revisiting that: "if a third call site ever needs this, that's the trigger
// to reconsider". Copy is that third call site.

/**
 * Splits a file name into its base and its extension, on the LAST dot.
 *
 * `dot <= 0` covers both "no dot at all" (README) and a leading-dot dotfile (.gitignore): neither has
 * an extension in the sense that matters here, so the whole name stays as the base and nothing is
 * hidden from the user when a name is offered for editing.
 */
export const splitName = (name: string): { base: string; ext: string } => {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return { base: name, ext: '' };
  return { base: name.slice(0, dot), ext: name.slice(dot + 1) };
};

/**
 * Reassembles a full name from an edited base and the original extension — the inverse of splitName
 * for the round trip a name-editing modal makes. An empty `ext` (a directory, a dotfile, an
 * extensionless file) yields the base unchanged rather than a name with a trailing dot.
 */
export const withExtension = (base: string, ext: string): string =>
  ext ? `${base}.${ext}` : base;
